import { useEffect, useRef, useCallback, useMemo } from 'react';
import { CandleData, AppMode } from '../types';

// Stream class interfaces
interface StreamInstance {
  close: () => void;
}

interface BinanceStreamConstructor {
  new (symbol: string, timeframe: string, dataSource: string, onTick: (candle: CandleData) => void): StreamInstance;
}

interface StockStreamConstructor {
  new (symbol: string, timeframe: string, onCandle: (candle: any) => void, initialCandles?: CandleData[]): StreamInstance;
}

interface UseWebSocketUpdatesParams {
  mode: AppMode;
  liveTicker: string;
  timeframe: string;
  dataSource: string;
  replayMode: 'inactive' | 'selecting' | 'active';
  layoutType: string;
  panels: Array<{ id: string; symbol: string; timeframe: string }>;
  candlesLength: number; // Number of candles loaded - used to prevent WebSocket on home screen
  onCandleUpdate: (candle: CandleData) => void;
  onChartTickUpdate?: (candle: CandleData) => void;
  onPanelDataUpdate?: (updater: (prev: Map<string, CandleData[]>) => Map<string, CandleData[]>) => void;
  fetchCandleData?: (symbol: string, timeframe: string) => Promise<{ data: CandleData[] }>;
  BinanceStream: BinanceStreamConstructor;
  StockStream: StockStreamConstructor;}

/**
 * Custom hook for managing WebSocket connections for real-time market data updates.
 * 
 * Handles:
 * - Binance WebSocket streams for crypto data
 * - Stock streams (Schwab) for equity data
 * - Polling fallback for other data sources
 * - Multi-panel updates for different timeframes
 * - Replay mode detection and prevention of live updates
 * 
 * @param params Configuration object for WebSocket connections
 * @returns Cleanup function is handled automatically by useEffect
 */
export function useWebSocketUpdates({
  mode,
  liveTicker,
  timeframe,
  dataSource,
  replayMode,
  layoutType,
  panels,
  candlesLength,
  onCandleUpdate,
  onChartTickUpdate,
  onPanelDataUpdate,
  fetchCandleData,
  BinanceStream,
  StockStream
}: UseWebSocketUpdatesParams): void {
  // Use ref to access latest replayMode value in callbacks without re-creating them
  const replayModeRef = useRef(replayMode);
  
  // Keep panels ref updated for use in callbacks
  const panelsRef = useRef(panels);
  panelsRef.current = panels;
  
  // Create a stable key based on panel symbols and timeframes to trigger effect when they change
  const panelStreamKey = useMemo(() => {
    return panels.map(p => `${p.id}:${p.symbol}:${p.timeframe}`).sort().join('|');
  }, [panels]);
  
  // Persistent refs for stream management (survive re-renders)
  const mainStreamRef = useRef<StreamInstance | null>(null);
  const panelStreamsRef = useRef<Map<string, StreamInstance>>(new Map());
  const pollingIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentMainTickerRef = useRef<string>('');
  const currentDataSourceRef = useRef<string>('');
  
  useEffect(() => {
    replayModeRef.current = replayMode;
  }, [replayMode]);

  // Handler for updating panel data - defined outside useEffect to be reusable
  const handlePanelUpdate = useCallback((symbol: string, candle: CandleData, candleTimeframe: string) => {
    // Block updates during replay mode - replay manages its own data
    if (replayModeRef.current !== 'inactive') {
      return;
    }
    
    // Validate candle has valid timestamp
    if (!candle.time || candle.time <= 0) {
      console.warn(`[useWebSocketUpdates] Ignoring invalid candle with time=${candle.time}`);
      return;
    }
    
    const currentPanels = panelsRef.current;
    console.log(`[useWebSocketUpdates] handlePanelUpdate called:`, { symbol, candleTimeframe, panelCount: currentPanels.length });
    
    if (layoutType !== '1x1' && currentPanels.length > 0 && onPanelDataUpdate) {
      // Find matching panels for this symbol (any timeframe - we'll aggregate)
      const matchingPanels = currentPanels.filter(p => p.symbol === symbol);
      console.log(`[useWebSocketUpdates] Matching panels for ${symbol}:`, matchingPanels.map(p => ({ id: p.id, tf: p.timeframe })));
      
      onPanelDataUpdate(prev => {
        const newMap = new Map(prev);
        matchingPanels.forEach(panel => {
          // Only update if candle timeframe matches panel timeframe
          if (panel.timeframe === candleTimeframe) {
            const existingData = prev.get(panel.id) || [];
            const lastIndex = existingData.length - 1;
            
            console.log(`[useWebSocketUpdates] Updating panel ${panel.id} (${panel.symbol}:${panel.timeframe}) with candle`);
            
            if (existingData.length === 0) {
              newMap.set(panel.id, [candle]);
            } else {
              const last = existingData[lastIndex];
              if (candle.time === last.time) {
                // Update existing candle
                const updated = [...existingData];
                updated[lastIndex] = candle;
                newMap.set(panel.id, updated);
              } else if (candle.time > last.time) {
                // Add new candle
                newMap.set(panel.id, [...existingData, candle]);
              }
            }
          } else {
            console.log(`[useWebSocketUpdates] Skipping panel ${panel.id} - timeframe mismatch (panel: ${panel.timeframe}, candle: ${candleTimeframe})`);
          }
        });
        return newMap;
      });
    }
  }, [layoutType, onPanelDataUpdate]);

  const updateCandles = useCallback((candle: CandleData) => {
    // IGNORE live updates if in Replay Mode (use ref for immediate access)
    if (replayModeRef.current === 'active' || replayModeRef.current === 'selecting') {
      console.log('[useWebSocketUpdates] 🚫 Ignoring live update - replay mode active');
      return;
    }

    onCandleUpdate(candle);

    // Optionally forward raw candle updates to chart layer for direct tick rendering
    if (onChartTickUpdate) {
      onChartTickUpdate(candle);
    }
  }, [onCandleUpdate, onChartTickUpdate]);

  // Main effect for primary ticker stream
  useEffect(() => {
    // Don't start WebSocket updates if in replay mode
    if (replayMode === 'active' || replayMode === 'selecting') {
      return;
    }
    
    // Don't start WebSocket if no candles loaded (home screen)
    // This prevents WebSocket from starting when user just changes timeframe on home screen
    if (candlesLength === 0) {
      return;
    }
    
    console.log(`[useWebSocketUpdates] Main effect - mode: ${mode}, ticker: ${liveTicker}, dataSource: ${dataSource}, replayMode: ${replayMode}`);
    
    // Close existing main stream if ticker or dataSource changed
    if (mainStreamRef.current && (currentMainTickerRef.current !== liveTicker || currentDataSourceRef.current !== dataSource)) {
      console.log(`[useWebSocketUpdates] Closing old main stream for ${currentMainTickerRef.current}`);
      mainStreamRef.current.close();
      mainStreamRef.current = null;
    }
    
    // Start WebSocket if we have a ticker
    // Note: We start WebSocket even if dataSource is 'unknown' because the backend may still stream data
    if (mode === AppMode.LIVE && liveTicker) {
      currentMainTickerRef.current = liveTicker;
      currentDataSourceRef.current = dataSource;
      
      if (dataSource.includes('binance')) {
        console.log(`[useWebSocketUpdates] Starting Binance WebSocket for ${liveTicker} (${dataSource})`);
        mainStreamRef.current = new BinanceStream(liveTicker, timeframe, dataSource, (candle) => {
          console.log(`[useWebSocketUpdates] WebSocket Update: ${candle.close}`);
          updateCandles(candle);
        });
      } else {
        // For stocks or unknown sources, use StockStream which connects to backend WebSocket
        console.log(`[useWebSocketUpdates] Starting StockStream for ${liveTicker} (dataSource: ${dataSource})`);
        
        mainStreamRef.current = new StockStream(liveTicker, timeframe, (candle: CandleData) => {
          // Update main chart
          updateCandles(candle);
        });
      }
    }

    return () => {
      if (mainStreamRef.current) {
        console.log(`[useWebSocketUpdates] Cleanup - closing main stream for ${liveTicker}`);
        mainStreamRef.current.close();
        mainStreamRef.current = null;
      }
      if (pollingIdRef.current) {
        clearInterval(pollingIdRef.current);
        pollingIdRef.current = null;
      }
    };
  }, [mode, liveTicker, timeframe, dataSource, replayMode, candlesLength, updateCandles, handlePanelUpdate, fetchCandleData, BinanceStream, StockStream]);

  // Separate effect for panel symbol subscriptions - runs when panel symbols change
  useEffect(() => {
    if (replayMode === 'active' || replayMode === 'selecting') {
      return;
    }
    
    // Don't start panel subscriptions if no candles loaded (home screen)
    if (candlesLength === 0) {
      return;
    }
    
    console.log(`[useWebSocketUpdates] Panel effect running:`, { replayMode, layoutType, dataSource, panelCount: panelsRef.current.length });
    
    if (layoutType === '1x1' || !panelsRef.current.length) {
      // Close all panel streams if not in multi-layout mode
      console.log(`[useWebSocketUpdates] Skipping panel subscriptions - layoutType: ${layoutType}, panels: ${panelsRef.current.length}`);
      panelStreamsRef.current.forEach((stream, symbol) => {
        console.log(`[useWebSocketUpdates] Closing panel stream for ${symbol} (not in multi-layout)`);
        stream.close();
      });
      panelStreamsRef.current.clear();
      return;
    }
    
    // Allow schwab, local-db, dukascopy-db, or unknown (for initial load)
    const validDataSource = dataSource.includes('schwab') || dataSource === 'local-db' || dataSource === 'dukascopy-db' || dataSource === 'unknown';
    if (!validDataSource) {
      console.log(`[useWebSocketUpdates] Skipping panel subscriptions - invalid dataSource: ${dataSource}`);
      return;
    }
    
    // Get current unique symbol:timeframe combinations from panels
    // We need separate streams for:
    // 1. Symbols different from main ticker
    // 2. Same symbol as main ticker but different timeframe (main stream won't provide correct TF data)
    const panelStreamKeys = new Set<string>();
    panelsRef.current.forEach(p => {
      if (!p.symbol || !p.symbol.trim()) return;
      
      // If same symbol as main ticker but different timeframe, we need a separate stream
      // If different symbol, we always need a stream
      if (p.symbol !== liveTicker || p.timeframe !== timeframe) {
        // Use symbol:timeframe as key to track unique streams needed
        const key = `${p.symbol}:${p.timeframe}`;
        panelStreamKeys.add(key);
      }
    });
    
    console.log(`[useWebSocketUpdates] Panel streams needed:`, {
      panelStreamKeys: [...panelStreamKeys],
      liveTicker,
      mainTimeframe: timeframe,
      subscribedPanelStreams: [...panelStreamsRef.current.keys()]
    });
    
    // Get currently subscribed stream keys (symbol:timeframe)
    const subscribedKeys = new Set(panelStreamsRef.current.keys());
    
    // Find streams to unsubscribe (no longer needed by any panel)
    const keysToUnsubscribe = [...subscribedKeys].filter(k => !panelStreamKeys.has(k));
    
    // Find streams to subscribe (new streams needed)
    const keysToSubscribe = [...panelStreamKeys].filter(k => !subscribedKeys.has(k));
    
    // Unsubscribe from streams no longer needed
    keysToUnsubscribe.forEach((key: string) => {
      const stream = panelStreamsRef.current.get(key);
      if (stream) {
        console.log(`[useWebSocketUpdates] 🔴 Unsubscribing from ${key} (no longer needed)`);
        stream.close();
        panelStreamsRef.current.delete(key);
      }
    });
    
    // Subscribe to new streams
    console.log(`[useWebSocketUpdates] Streams to subscribe: ${keysToSubscribe.length}`, keysToSubscribe);
    
    keysToSubscribe.forEach((key: string) => {
      const [symbol, streamTimeframe] = key.split(':');
      
      console.log(`[useWebSocketUpdates] 🟢 Subscribing to ${symbol} (${streamTimeframe}) for panel updates`);
      const stream = new StockStream(symbol, streamTimeframe, (candle: CandleData) => {
        console.log(`[useWebSocketUpdates] 📥 Received tick for ${symbol} (${streamTimeframe})`);
        handlePanelUpdate(symbol, candle, streamTimeframe);
      });
      panelStreamsRef.current.set(key, stream);
      console.log(`[useWebSocketUpdates] Stream created for ${key}, active streams:`, [...panelStreamsRef.current.keys()]);
    });
    
    if (keysToUnsubscribe.length > 0 || keysToSubscribe.length > 0) {
      console.log(`[useWebSocketUpdates] Panel streams updated:`, {
        unsubscribed: keysToUnsubscribe,
        subscribed: keysToSubscribe,
        active: [...panelStreamsRef.current.keys()]
      });
    }
    
    // NOTE: We don't return a cleanup function here because we manage streams incrementally.
    // Cleanup only happens on component unmount via the main effect or when layoutType changes to 1x1.
  }, [panelStreamKey, layoutType, liveTicker, timeframe, dataSource, replayMode, candlesLength, handlePanelUpdate, StockStream]);
  
  // Cleanup all panel streams on unmount
  useEffect(() => {
    return () => {
      console.log(`[useWebSocketUpdates] Component unmount - closing all panel streams`);
      panelStreamsRef.current.forEach((stream, symbol) => {
        console.log(`[useWebSocketUpdates] Cleanup - closing panel stream for ${symbol}`);
        stream.close();
      });
      panelStreamsRef.current.clear();
    };
  }, []);
}
